import { MoreVert, Router } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, MenuItem, Menu as MuiMenu } from '@mui/material';
import type { ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDeleteDeviceGroup } from '~/api/device-groups';
import { IconButton } from '~/components/IconButton';
import { ActionButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreDeleteAction,
  mergeGridActionProps,
  type GridActionCellProps,
} from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import type { NetworkDeviceGroups__DeviceGroup as DeviceGroup } from '../../queries/list.generated';

export interface MenuProps extends GridActionCellProps {
  buttonType?: 'action' | 'icon';
  deviceGroup: DeviceGroup;
  Icon?: ElementType;
}

export const Menu = ({
  buttonType = 'icon',
  deviceGroup,
  Icon = MoreVert,
  ...cellProps
}: MenuProps) => {
  const { t } = useTranslation();
  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<DeviceGroup>();
  const deviceGroupActionProps = mergeGridActionProps(moreTableActionProps(deviceGroup), cellProps);

  const link = useLink();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [deleteDeviceGroup] = useDeleteDeviceGroup();

  return (
    <>
      {buttonType === 'icon' ? (
        <IconButton
          label={t('common.actions.actions')}
          color="secondary"
          onClick={deviceGroupActionProps.onClick}
          size="small"
        >
          <Icon />
        </IconButton>
      ) : (
        <ActionButton {...deviceGroupActionProps} />
      )}
      <MuiMenu {...moreMenuProps}>
        <MenuItem
          component={Link}
          to={link(`/device-groups/${moreMenuProps.context?.id}/devices`)}
          state={true}
        >
          <ListItemIcon>
            <Router />
          </ListItemIcon>
          <ListItemText>{t('common.labels.devices')}</ListItemText>
        </MenuItem>
        <Divider />
        <MoreDeleteAction
          {...moreActionProps}
          tooltip={
            moreMenuProps.context?.canDestroy.value
              ? t('pages.device_groups.list.menu.delete_title')
              : moreMenuProps.context?.canDestroy.reasons?.fullMessages.join(', ') ||
                t('pages.device_groups.list.menu.cannot_delete_tooltip')
          }
          disabled={!moreMenuProps.context?.canDestroy.value}
          onClick={async () => {
            assert(moreMenuProps.context !== undefined, 'Data missing from context');
            if (!(await confirmDelete())) return;
            await deleteDeviceGroup({
              variables: {
                input: { deviceGroupIds: [moreMenuProps.context.id] },
              },
            });
          }}
        />
      </MuiMenu>

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('pages.device_groups.list.menu.delete_confirm')}
        deleteConfirm
        prompt={
          <span>
            {t('pages.device_groups.list.menu.delete_prompt')}{' '}
            <b>{t('common.warnings.cannot_be_undone')}</b>
          </span>
        }
        title={t('pages.device_groups.list.menu.delete_title')}
      />
    </>
  );
};
